import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowLeft, Earth } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Tilt } from '@/components/generals/tilt'
import { SectionHeading } from '@/components/generals/section-heading'
import { EXPORT_REGIONS, SHIPPING_MODES } from '@/constants/content'
import { MEDIA } from '@/constants/media'

export const ExportSection: FC = (): ReactNode => {
    return (
        <section id='export' aria-labelledby='export-title' className='py-24 lg:py-32'>
            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-12'>
                <div className='lg:col-span-6'>
                    <SectionHeading
                        id='export-title'
                        eyebrow='صادرات'
                        title='از ایران، به بازارهای جهان'
                        description='محصولات خوزکیمیا زاگرس با مدارک کامل صادراتی، از راه هوایی، دریایی یا زمینی به خریداران در شش منطقه جهان ارسال می‌شود.'
                    />

                    <ul className='mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3' aria-label='مناطق صادراتی'>
                        {EXPORT_REGIONS.map(region => (
                            <li
                                key={region.latin}
                                className='flex items-center gap-3 rounded-2xl border border-sand bg-card px-4 py-3'
                            >
                                <Earth aria-hidden='true' className='size-5 shrink-0 text-gold-deep' strokeWidth={1.6} />

                                <span className='flex flex-col leading-tight'>
                                    <span className='font-heading text-lg font-bold text-forest-900'>{region.name}</span>
                                    <span className='text-[0.7rem] uppercase tracking-wider text-muted-foreground'>{region.latin}</span>
                                </span>
                            </li>
                        ))}
                    </ul>

                    <ul className='mt-8 grid gap-5 sm:grid-cols-3'>
                        {SHIPPING_MODES.map(({ icon: Icon, title, text }) => (
                            <li key={title} className='flex flex-col gap-2'>
                                <span className='inline-flex items-center gap-2 font-semibold text-forest-800'>
                                    <Icon aria-hidden='true' className='size-5 text-gold-deep' strokeWidth={1.6} />
                                    {title}
                                </span>

                                <span className='text-sm leading-6 text-muted-foreground'>
                                    {text}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <ButtonLink href='/export' variant='primary' className='mt-10'>
                        خدمات صادرات و خرید عمده
                        <ArrowLeft aria-hidden='true' />
                    </ButtonLink>
                </div>

                <div className='reveal lg:col-span-6'>
                    <Tilt max={5} className='rounded-[2rem]'>
                        <div className='relative overflow-hidden rounded-[2rem] border border-sand bg-forest-900 shadow-[0_40px_80px_-45px_rgba(15,42,30,0.7)]'>
                            <Image
                                src={MEDIA.globalExportFa}
                                alt='صادرات جهانی محصولات گیاهی خوزکیمیا زاگرس از کوه‌های زاگرس به اروپا، آسیا، خاورمیانه، آفریقا، آمریکا و استرالیا'
                                width={1536}
                                height={1024}
                                sizes='(min-width: 1024px) 45vw, 92vw'
                                className='h-auto w-full'
                            />
                        </div>
                    </Tilt>
                </div>
            </div>
        </section>
    )
}
